/**
 * Reading text out loud, in the browser, for nothing.
 *
 * ── Why the browser and not a model ──────────────────────────────────────────
 *
 * `speechSynthesis` ships in every current browser, needs no key, no request
 * and no credit, and every desktop and phone already carries voices for the
 * languages its owner uses. A hosted TTS would sound better and would put a
 * per-character bill on a button people press out of curiosity — on a free
 * product with no sign-in, that is the wrong trade. This is the same reasoning
 * that put dictation on the browser's own recogniser first (see lib/liveSpeech)
 * and it fails the same way: gracefully, by not offering the button.
 *
 * ── The three ways it breaks, all of which are handled here ──────────────────
 *
 * 1. **Voices arrive late.** `getVoices()` is empty on first call in Chrome and
 *    fills in a tick later, announced by `voiceschanged`. Code that reads it
 *    once at module load concludes the browser cannot speak at all — and then
 *    the button never appears for anybody.
 * 2. **Long utterances stop early.** Chrome cuts an utterance somewhere past a
 *    few hundred characters, and there is no error: the voice simply stops mid
 *    sentence. So the text is spoken as a QUEUE of sentence-sized pieces, cut
 *    at the boundaries `paginate` already knows how to find — never mid
 *    sentence, so the pauses land where a reader would put them anyway.
 * 3. **The engine falls asleep.** Chrome pauses synthesis after roughly fifteen
 *    seconds of speaking. The long-standing workaround is to `resume()` it on a
 *    timer while it is meant to be talking; it is a no-op when nothing is
 *    paused, which is why it is safe to run blind.
 *
 * ── Cancelling ───────────────────────────────────────────────────────────────
 *
 * Every start cancels whatever was already speaking. Two voices reading two
 * different texts over each other is the one outcome nobody wants, and the
 * queue is global to the page — a second button cannot politely wait its turn.
 */

import { paginate } from './paginate';

/**
 * How much text goes into one utterance.
 *
 * Comfortably under where Chrome starts truncating, and `paginate` will cut
 * earlier still if that is where a sentence ends.
 */
const CHUNK = 180;

/** How often to poke a possibly-asleep engine. Chrome's nap is ~15s. */
const KEEPALIVE_MS = 7_000;

export function speechSupported(): boolean {
  return typeof window !== 'undefined' && 'speechSynthesis' in window && 'SpeechSynthesisUtterance' in window;
}

/** `ko-KR`, `ko_kr`, `KO` → `ko-kr`, so tags from different sources compare. */
function norm(tag: string): string {
  return tag.trim().toLowerCase().replace('_', '-');
}

/**
 * The best voice for a tag, or null to let the browser choose from the tag
 * alone.
 *
 * Exact region first (`ko-KR` over `ko`), then the language whatever its region
 * — a Brazilian voice reading European Portuguese is a far better answer than
 * silence — and the browser's own default within each group, since that is the
 * one its owner has already been listening to everywhere else.
 */
export function voiceFor(tag: string): SpeechSynthesisVoice | null {
  if (!speechSupported() || !tag) return null;
  const voices = window.speechSynthesis.getVoices();
  if (!voices.length) return null;

  const want = norm(tag);
  const base = want.split('-')[0];
  const exact = voices.filter((v) => norm(v.lang) === want);
  const family = voices.filter((v) => norm(v.lang).split('-')[0] === base);

  return (
    exact.find((v) => v.default) ??
    exact[0] ??
    family.find((v) => v.default) ??
    family[0] ??
    null
  );
}

/** Can this browser speak this language at all? */
export function canSpeak(tag: string): boolean {
  return !!voiceFor(tag);
}

/**
 * Run `cb` whenever the voice list changes, and once now.
 *
 * The subscription is the point: on a cold Chrome the first answer is "no
 * voices, cannot speak", and it becomes "here are forty" a moment later. A
 * button that decided at mount stays wrong until the page is reloaded.
 */
export function onVoicesChanged(cb: () => void): () => void {
  if (!speechSupported()) return () => {};
  const synth = window.speechSynthesis;
  cb();
  // Some builds expose the event only as a property.
  synth.addEventListener?.('voiceschanged', cb);
  const prev = synth.onvoiceschanged;
  if (!synth.addEventListener) synth.onvoiceschanged = cb;
  return () => {
    synth.removeEventListener?.('voiceschanged', cb);
    if (!synth.addEventListener) synth.onvoiceschanged = prev ?? null;
  };
}

export interface Speaking {
  /** Stop now and drop everything still queued. */
  stop(): void;
}

/** Cancel whatever the page is currently saying, if anything. */
export function stopSpeaking(): void {
  if (speechSupported()) window.speechSynthesis.cancel();
}

/**
 * Say `text` in `tag`, a piece at a time.
 *
 * `onEnd` fires once, whether the text finished, an utterance failed, or
 * somebody pressed stop — the button has one resting state and gets back to it
 * by exactly one path.
 */
export function speak(
  text: string,
  tag: string,
  handlers: { onEnd?: () => void; onError?: (message: string) => void } = {},
): Speaking {
  const synth = window.speechSynthesis;
  const pieces = paginate(text.trim(), CHUNK)
    .map((p) => p.text.trim())
    .filter(Boolean);

  let stopped = false;
  let keepalive: ReturnType<typeof setInterval> | null = null;

  const finish = () => {
    if (stopped) return;
    stopped = true;
    if (keepalive) clearInterval(keepalive);
    handlers.onEnd?.();
  };

  // Whatever was speaking is not what was just asked for.
  synth.cancel();

  if (!pieces.length) {
    handlers.onEnd?.();
    return { stop: () => {} };
  }

  const voice = voiceFor(tag);
  pieces.forEach((piece, i) => {
    const u = new SpeechSynthesisUtterance(piece);
    // The tag goes on even when a voice was found: some engines read it rather
    // than the voice's own, and the two never disagree here.
    if (tag) u.lang = tag;
    if (voice) u.voice = voice;
    if (i === pieces.length - 1) u.onend = finish;
    u.onerror = (e) => {
      // `interrupted` and `canceled` are what `cancel()` produces — this is the
      // stop button working, not a failure to report.
      const kind = (e as SpeechSynthesisErrorEvent).error;
      if (kind && kind !== 'interrupted' && kind !== 'canceled') {
        handlers.onError?.('This browser could not read that out.');
      }
      finish();
    };
    synth.speak(u);
  });

  keepalive = setInterval(() => {
    if (stopped) return;
    // A no-op unless Chrome has quietly paused itself mid-queue.
    if (synth.speaking) synth.resume();
  }, KEEPALIVE_MS);

  return {
    stop() {
      synth.cancel();
      finish();
    },
  };
}
